import { useMemo } from 'react';

import { z } from 'zod';

import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useStartAPetitionPageState } from '#src/app/pages/startAPetitionV2/pageContext';

import type { SandboxContract } from '../../types';

type SandboxActions = SandboxContract['actions'];
type SubmitParams = Parameters<SandboxActions['submit']>[0];
type UpdateParams = Parameters<SandboxActions['update']>[0];
type TrackingParams = Parameters<SandboxActions['track']>[0];
type ErrorParams = Parameters<SandboxActions['error']>[0];

/**
 * Overrides how the child's `submit` message is handled. Receives the raw params
 * plus the real `submit` action, and decides if/when to call it — e.g. to gate on
 * an async step (a mutation) before committing to the state machine. When omitted,
 * `submit` dispatches directly, same as every other sandbox action.
 */
type OnSubmitOverride = (params: SubmitParams, submit: SandboxActions['submit']) => void | Promise<void>;
/**
 * Side effect for a child-reported `error`. The channel always validates and reports
 * the error itself; this only decides what *else* happens (e.g. switch on `code` and
 * dispatch a state-machine event). Swappable — omit it and the error is reported only.
 */
type OnErrorHandler = (params: ErrorParams) => void;

type SandboxHandlers = { onSubmit?: OnSubmitOverride; onError?: OnErrorHandler };

// Shallow validation only: the envelope is checked, `code` must be a string. Unknown
// fields are stripped (default zod behavior) rather than passed through — the child
// only ever sends `code`/`recoverable`, and stripping means there's nothing extra for
// a report to leak even if that changes.
const errorParamsSchema = z.object({ code: z.string(), recoverable: z.boolean().optional() });

export function useSandbox({ onSubmit, onError }: SandboxHandlers = {}): SandboxContract {
	const { data, actions } = useStartAPetitionPageState();
	const track = useTracking();
	const { errorReporter } = useUtilityContext();

	const sandboxData = useMemo(() => ({ draft: data.draft ?? {} }), [data.draft]);

	const sandboxActions = useMemo(() => {
		const submit: SandboxActions['submit'] = ({ draft, target }) => actions.send({ type: 'submit', draft, target });

		return {
			back: () => actions.send({ type: 'back' }),
			update: ({ draft }: UpdateParams) => actions.send({ type: 'submit', target: 'self', draft }),
			submit: onSubmit
				? (params: SubmitParams) => {
						// onSubmit may throw synchronously or reject asynchronously (e.g. a mutation);
						// wrapping the call itself in the async IIFE (rather than just its result)
						// catches both, since there's no UI at this layer to recover from an onSubmit
						// failure itself.
						(async () => onSubmit(params, submit))().catch((error: unknown) => {
							void errorReporter.report(new Error('SAP: sandbox onSubmit failed', { cause: error }));
						});
					}
				: submit,
			track: ({ eventName, eventData }: TrackingParams) => void track(eventName, eventData),
			error: (params: ErrorParams) => {
				const parsed = errorParamsSchema.safeParse(params);
				// every child error is reported, whether or not the payload is well-formed. One
				// constant message keeps every child error in the same Sentry group; `code` rides
				// along as a tag — a synthetic 'malformed_payload' value when validation failed —
				// so both real codes and malformed payloads stay facetable. A `recoverable` error
				// is downgraded to `warning` — the child already retries, so it shouldn't page at
				// the same severity as one that isn't.
				void errorReporter.report(
					{
						error: new Error('SAP: sandbox child error'),
						context: { code: parsed.success ? parsed.data.code : 'malformed_payload' },
					},
					{ severity: parsed.success && parsed.data.recoverable ? 'warning' : 'error' },
				);
				// the side effect only runs for a valid payload — it switches on `code`
				if (parsed.success) onError?.(parsed.data);
			},
		};
	}, [actions, track, onSubmit, onError, errorReporter]);

	return { data: sandboxData, actions: sandboxActions };
}
